import { Component } from 'react'
import { reportError } from '../lib/monitoring'
import { isStaleChunkError, recoverFromStaleChunk, clearUpdatingSplash } from '../lib/staleChunk'

// ── ErrorBoundary ─────────────────────────────────────────────────────────────
// Catches render errors in child components and shows a graceful fallback.
// Wrap around any component that fetches external data or could fail.

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    // A stale-chunk failure (old PWA shell hit a hashed chunk that a deploy replaced)
    // isn't a real app error — don't flash the scary card; render nothing while the
    // recovery reload takes over.
    return { hasError: true, error, recovering: isStaleChunkError(error) }
  }

  componentDidCatch(error, info) {
    // After a deploy, a lazy import can fail with "'text/html' is not a valid JavaScript
    // MIME type" — auto-recover (clear caches + fresh reload) instead of stranding the
    // user on the error card. Guarded so it can't loop (2026-07-21).
    if (recoverFromStaleChunk(error)) {
      this.recoverTimer = setTimeout(() => { clearUpdatingSplash(); this.setState({ recovering: false }) }, 10000)
      return
    }
    if (this.state.recovering) this.setState({ recovering: false })
    console.error('[ErrorBoundary]', error, info.componentStack)
    // Surface to monitoring (no-op unless a Sentry DSN is configured).
    reportError(error, { componentStack: info?.componentStack, boundary: this.props.label || 'root' })
  }

  componentWillUnmount() { clearTimeout(this.recoverTimer) }

  render() {
    if (!this.state.hasError) return this.props.children
    // Stale-chunk recovery is reloading — show nothing rather than a flash of the card.
    if (this.state.recovering) return null

    const { fallback, label = 'this section' } = this.props

    // Explicit fallback wins — including `fallback={null}`, which means "render
    // nothing" (used to gracefully HIDE a non-critical section that failed, rather
    // than show a scary error card). Only an absent prop falls through to the card.
    if (fallback !== undefined) return fallback

    return (
      <div style={{
        padding: '16px',
        background: 'color-mix(in srgb, var(--state-bad) 6%, transparent)',
        border: '1px solid color-mix(in srgb, var(--state-bad) 18%, transparent)',
        borderRadius:'var(--radius)',
        margin: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'var(--font)',
      }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>
            Something went wrong with {label}
          </div>
          {/* The actual error, without it, users can only report "there was
              an error" with nothing actionable to pass along. */}
          {this.state.error?.message && (
            <div style={{ color: 'var(--text-dim)', fontSize: 10.5, marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-word' }}>
              {String(this.state.error.message).slice(0, 200)}
            </div>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              marginTop: 5,
              background: 'none',
              border: 'none',
              color: 'color-mix(in srgb, var(--state-bad) 80%, transparent)',
              fontSize: 11,
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'var(--font)',
              fontWeight: 500,
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 5, marginLeft: 14,
              background: 'none', border: 'none',
              color: 'var(--text-dim)', fontSize: 11,
              cursor: 'pointer', padding: 0,
              fontFamily: 'var(--font)', fontWeight: 500,
            }}
          >
            Reload app
          </button>
        </div>
      </div>
    )
  }
}
